import { useState, useRef, useCallback } from 'react';
import AppUtils from '@control-front-end/utils/utils';
import { MOUSE_BUTTONS } from '@control-front-end/common/constants';

/**
 * A custom hook used for implementing a text selection quoting feature
 * within a specified container element. It manages the selection of text,
 * popup display for actions, and interaction handling for quoting or
 * clearing the selection.
 *
 * @function useQuoting
 * @param {Object} params - The parameters passed to the hook.
 * @param {React.RefObject} params.containerRef - A React ref pointing to the container
 * element where text selection is monitored.
 * @param {Function} params.onQuote - A callback function triggered when the "quote" action
 * is performed, with the selected text as its argument.
 * @returns {Object} - Returns an object containing:
 * - `selectedText` (Object): An object holding the selected text and its index.
 * - `popup` (boolean): A boolean indicating if the popup is visible.
 * - `popupStyle` (Object): An object defining the style of the popup (position).
 * - `popupRef` (React.RefObject): A React ref for the popup element.
 * - `containerRef` (React.RefObject): A React ref for the container element.
 * - `handleMouseUp` (Function): A function for handling mouse up events
 * - `handleMouseDown` (Function): A function for handling mouse down events
 * - `handleQuoteClick` (Function): A function to handle the clicking the quote button
 * - `clearSelection` (Function): A function to clear the selection and hide the popup.
 */
const useQuoting = ({ containerRef, onQuote }) => {
  const [selectedText, setSelectedText] = useState({ text: '', index: null });
  const [popup, togglePopup] = useState(false);
  const [popupStyle, setPopupStyle] = useState({ top: 0, left: 0 });
  const rangeRef = useRef();
  const popupRef = useRef();

  const getRange = () => {
    const sel = window.getSelection();
    if (sel.rangeCount === 0) return null;
    return sel.getRangeAt(0);
  };

  const handleMouseUp = useCallback(
    /**
     * Handles an event to show a popup when text is selected within a specific container element.
     * The function prevents the click event from propagating further, extracts the selected text,
     * calculates the position to display the popup, determines the index of the selected text,
     * and sets the style and state for the popup positioning.
     *
     * @param {Event} e - The event object triggered by the user interaction.
     */
    (e) => {
      e.stopPropagation();
      const text = AppUtils.getSelectedText();
      if (!text.trim().length) return;

      const range = getRange();
      const containerEl = containerRef?.current;
      if (!containerEl) return;

      const relative = containerEl.getBoundingClientRect();
      let left;
      rangeRef.current = range;

      if (rangeRef.current.getClientRects) {
        const rect = rangeRef.current.getClientRects()[0];
        left = (rect.right + rect.left) / 2;
      } else {
        left = e.clientX - relative.left < 0 ? 0 : e.clientX;
      }

      const top = e.clientY + 10;

      const textContent =
        containerEl.textContent || containerEl.innerText || '';

      setSelectedText({
        text,
        index: AppUtils.getSelectedTextIndex({
          descriptionText: textContent,
          descriptionEl: containerEl,
          selectedText: text,
          range,
        }),
        sourceId: AppUtils.getSelectedTextSourceId(),
      });

      togglePopup(true);
      setPopupStyle({ top: top - relative.top, left: left - relative.left });
    },
    [containerRef?.current]
  );

  const handleMouseDown = useCallback((e) => {
    if (popupRef.current && popupRef.current.contains(e.target)) return;
    togglePopup(false);
    if (e.button === MOUSE_BUTTONS.RIGHT) return;
    window.getSelection().removeAllRanges();
    setSelectedText({ text: '', index: null });
  }, []);

  const handleQuoteClick = useCallback(() => {
    onQuote(selectedText);
    togglePopup(false);
  }, [selectedText, onQuote]);

  const clearSelection = useCallback(() => {
    togglePopup(false);
    setSelectedText({ text: '', index: null });
  }, []);

  return {
    selectedText,
    popup,
    popupStyle,
    popupRef,
    containerRef,
    handleMouseUp,
    handleMouseDown,
    handleQuoteClick,
    clearSelection,
    storedRange: rangeRef.current,
  };
};

export default useQuoting;
